import { LAB_TIME_ZONE } from "./labTime";

declare const DATE_ONLY_BRAND: unique symbol;

export type DateOnlyString = string & {
  readonly [DATE_ONLY_BRAND]: "DateOnlyString";
};

export interface DateOnlyShortMonthOptions {
  omitYear?: boolean;
  twoDigitYear?: boolean;
}

interface DateOnlyComponents {
  day: number;
  month: number;
  year: number;
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MINIMUM_YEAR = 1;
const MAXIMUM_YEAR = 9999;
const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const LAB_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function isDateOnlyString(value: unknown): value is DateOnlyString {
  if (typeof value !== "string") return false;
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return false;

  return isValidCalendarDate({
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  });
}

export function parseDateOnly(value: unknown): DateOnlyString | undefined {
  return isDateOnlyString(value) ? value : undefined;
}

export function requireDateOnlyString(
  value: unknown,
  fieldName = "Date"
): DateOnlyString {
  const dateOnly = parseDateOnly(value);
  if (dateOnly) return dateOnly;
  throw new Error(`${fieldName} must be a valid YYYY-MM-DD calendar date`);
}

export function formatDateOnlyForDisplay(value: DateOnlyString): string {
  const { year, month, day } = getComponents(value);
  return `${padTwoDigits(month)}/${padTwoDigits(day)}/${padFourDigits(year)}`;
}

export function formatDateOnlyWithShortMonth(
  value: DateOnlyString,
  options: DateOnlyShortMonthOptions = {}
): string {
  const { year, month, day } = getComponents(value);
  const monthAndDay = `${SHORT_MONTH_NAMES[month - 1]} ${day}`;
  if (options.omitYear) return monthAndDay;

  const fullYear = padFourDigits(year);
  const displayedYear = options.twoDigitYear ? fullYear.slice(-2) : fullYear;
  return `${monthAndDay}, ${displayedYear}`;
}

export function addCalendarDays(
  value: DateOnlyString,
  days: number
): DateOnlyString | undefined {
  if (!Number.isSafeInteger(days)) return undefined;
  const components = getComponents(value);
  const targetDay = daysFromCivil(components) + days;
  if (!Number.isSafeInteger(targetDay)) return undefined;

  return buildDateOnly(civilFromDays(targetDay));
}

export function calendarDayDifference(
  from: DateOnlyString,
  to: DateOnlyString
): number {
  return daysFromCivil(getComponents(to)) - daysFromCivil(getComponents(from));
}

export function calculateAgeFromDateOnly(
  dateOfBirth: DateOnlyString,
  today: DateOnlyString
): number {
  const validatedDateOfBirth = requireDateOnlyString(
    dateOfBirth,
    "Date of birth"
  );
  const validatedToday = requireDateOnlyString(today, "Current lab date");
  const birthYear = Number(validatedDateOfBirth.slice(0, 4));
  const currentYear = Number(validatedToday.slice(0, 4));
  const birthMonthAndDay = validatedDateOfBirth.slice(5);
  const anniversaryMonthAndDay =
    birthMonthAndDay === "02-29" && !isLeapYear(currentYear)
      ? "02-28"
      : birthMonthAndDay;
  const birthdayHasOccurred = validatedToday.slice(5) >= anniversaryMonthAndDay;

  return currentYear - birthYear - (birthdayHasOccurred ? 0 : 1);
}

export function getLabTodayDateOnly(now: Date = new Date()): DateOnlyString {
  const parts = LAB_DATE_FORMATTER.formatToParts(now);
  const value = [
    getFormattedPart(parts, "year"),
    getFormattedPart(parts, "month"),
    getFormattedPart(parts, "day"),
  ].join("-");
  const dateOnly = parseDateOnly(value);
  if (!dateOnly)
    throw new Error("Lab calendar date formatter returned an invalid date");
  return dateOnly;
}

function getComponents(value: DateOnlyString): DateOnlyComponents {
  const validatedValue = requireDateOnlyString(value);
  const [year, month, day] = validatedValue.split("-").map(Number);
  return { year, month, day };
}

function isValidCalendarDate(components: DateOnlyComponents): boolean {
  const { year, month, day } = components;
  if (year < MINIMUM_YEAR || year > MAXIMUM_YEAR) return false;
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= daysInMonth(year, month);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function padTwoDigits(value: number): string {
  return String(value).padStart(2, "0");
}

function padFourDigits(value: number): string {
  return String(value).padStart(4, "0");
}

function buildDateOnly(
  components: DateOnlyComponents
): DateOnlyString | undefined {
  if (!isValidCalendarDate(components)) return undefined;
  const { year, month, day } = components;
  return parseDateOnly(
    `${padFourDigits(year)}-${padTwoDigits(month)}-${padTwoDigits(day)}`
  );
}

function daysFromCivil({ year, month, day }: DateOnlyComponents): number {
  const adjustedYear = year - (month <= 2 ? 1 : 0);
  const era = Math.floor(adjustedYear / 400);
  const yearOfEra = adjustedYear - era * 400;
  const adjustedMonth = month + (month > 2 ? -3 : 9);
  const dayOfYear = Math.floor((153 * adjustedMonth + 2) / 5) + day - 1;
  const leapDays = Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100);
  return era * 146097 + yearOfEra * 365 + leapDays + dayOfYear;
}

function civilFromDays(value: number): DateOnlyComponents {
  const era = Math.floor(value / 146097);
  const dayOfEra = value - era * 146097;
  const yearOfEra = getYearOfEra(dayOfEra);
  const year = yearOfEra + era * 400;
  const dayOfYear = dayOfEra - daysBeforeYear(yearOfEra);
  const adjustedMonth = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * adjustedMonth + 2) / 5) + 1;
  const month = adjustedMonth + (adjustedMonth < 10 ? 3 : -9);
  return { year: year + (month <= 2 ? 1 : 0), month, day };
}

function getYearOfEra(dayOfEra: number): number {
  return Math.floor(
    (dayOfEra -
      Math.floor(dayOfEra / 1460) +
      Math.floor(dayOfEra / 36524) -
      Math.floor(dayOfEra / 146096)) /
      365
  );
}

function daysBeforeYear(yearOfEra: number): number {
  return (
    yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100)
  );
}

function getFormattedPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string {
  const value = parts.find((part) => part.type === type)?.value;
  if (!value) throw new Error(`Lab calendar date is missing ${type}`);
  return value;
}
